import { AppearanceError } from './errors.js';

/*
 * Page layouts (docs/26 §5): the order of the sections on Order and Account, and which are on — the same pattern as
 * Home's sections (home.ts, Settings › Home). The Card and Offers pages went on 21 Oct 2026 (evening): the card and the
 * Rewards and Earn sheets live on Home now. Some sections can't be switched off, each for a reason the admin shows.
 * A section still shows only when it has something to show (signed in, its feature switch on, not empty): switching
 * it on never forces it.
 */

export const PAGE_IDS = ['menu', 'account'] as const;
export type PageId = (typeof PAGE_IDS)[number];

export interface SectionDef { id: string; label: string; sub: string; locked?: string }

export const PAGE_SECTIONS: Record<PageId, readonly SectionDef[]> = {
  menu: [
    { id: 'usuals', label: 'Your usuals', sub: 'Saved orders and favourites, compact' },
    { id: 'group', label: 'Catering and group order', sub: 'The two chips, when their switches are on' },
    { id: 'menu', label: 'The menu', sub: 'Categories and items', locked: 'It’s the menu.' },
  ],
  account: [
    { id: 'profile', label: 'Their details', sub: 'Name and phone number' },
    { id: 'creator', label: 'Creator hub', sub: 'Only creators see it: their code, earnings, credit and giveaways' },
    { id: 'orders', label: 'Order history', sub: 'App, website and till orders' },
    { id: 'saved', label: 'Saved orders', sub: 'Named orders and one-tap reorder' },
    { id: 'cards', label: 'Saved cards', sub: 'Cards kept with Stripe' },
    { id: 'notifications', label: 'Notifications', sub: 'Order updates and marketing choices', locked: 'Customers must always be able to change their marketing choices (UK GDPR and PECR).' },
    { id: 'messages', label: 'Messages', sub: 'Messages from Tikkas' },
    { id: 'invite', label: 'Invite a friend', sub: 'Their referral code' },
    { id: 'help', label: 'Contact us', sub: 'Help and support' },
    { id: 'legal', label: 'Privacy and terms', sub: 'The legal pages', locked: 'The legal pages must always be reachable.' },
    { id: 'sign_out', label: 'Sign out', sub: '', locked: 'Signing out must always be possible.' },
    { id: 'delete', label: 'Delete account', sub: 'Asks first', locked: 'Apple requires account deletion to be reachable in the app.' },
  ],
};

export const PAGE_NAMES: Record<PageId, string> = { menu: 'Order', account: 'Account' };

export interface SectionSetting { id: string; enabled: boolean }
export type PagesSetting = Record<PageId, SectionSetting[]>;

export const DEFAULT_PAGES: PagesSetting = Object.fromEntries(PAGE_IDS.map((p) => [p, PAGE_SECTIONS[p].map((s) => ({ id: s.id, enabled: true }))])) as PagesSetting;

/** The page layouts to save, checked: each page lists its sections once, each on or off, and the locked ones stay on. */
export function checkPages(raw: unknown): PagesSetting {
  const o = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const out = {} as PagesSetting;
  for (const p of PAGE_IDS) {
    const defs = PAGE_SECTIONS[p];
    const list = o[p];
    if (!Array.isArray(list) || list.length !== defs.length) throw new AppearanceError(`${PAGE_NAMES[p]}: list every section once`, 'bad_pages');
    const seen = new Set<string>();
    out[p] = list.map((x: unknown) => {
      const r = x && typeof x === 'object' ? (x as Record<string, unknown>) : {};
      const def = defs.find((d) => d.id === r.id);
      if (!def || seen.has(def.id)) throw new AppearanceError(`${PAGE_NAMES[p]}: list every section once`, 'bad_pages');
      seen.add(def.id);
      if (typeof r.enabled !== 'boolean') throw new AppearanceError(`${PAGE_NAMES[p]}: each section is on or off`, 'bad_pages');
      if (def.locked && !r.enabled) throw new AppearanceError(`${PAGE_NAMES[p]}: “${def.label}” can’t be switched off. ${def.locked}`, 'section_locked');
      return { id: def.id, enabled: r.enabled };
    });
  }
  return out;
}

/** Stored layouts, read leniently (like Home's blocks): the stored order kept, unknown ones dropped, a new one added at the end, locked ones on. */
export function storedPages(raw: unknown): PagesSetting {
  const o = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const out = {} as PagesSetting;
  for (const p of PAGE_IDS) {
    const defs = PAGE_SECTIONS[p];
    const list = Array.isArray(o[p]) ? (o[p] as unknown[]) : [];
    const kept: SectionSetting[] = [];
    for (const x of list) {
      const r = x && typeof x === 'object' ? (x as Record<string, unknown>) : {};
      const def = defs.find((d) => d.id === r.id);
      if (!def || kept.some((k) => k.id === def.id)) continue;
      kept.push({ id: def.id, enabled: def.locked ? true : r.enabled !== false });
    }
    for (const d of defs) if (!kept.some((k) => k.id === d.id)) kept.push({ id: d.id, enabled: true });
    out[p] = kept;
  }
  return out;
}

/** The section ids switched on, in order: what GET /api/appearance sends for a page. */
export const enabledSections = (pages: PagesSetting, page: PageId): string[] => pages[page].filter((s) => s.enabled).map((s) => s.id);
